import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/services/adminApi';
import { posthog } from '@/observability/posthog';
import { LogsFilterBar } from './logs/LogsFilterBar';
import { LogsTable } from './logs/LogsTable';
import type { LogQuery, LogsResponse, VendorStatus } from '@/types/logs';

const DEFAULT_QUERY: LogQuery = {
  source: 'both',
  since: '24h',
  provider: [],
  kind: [],
  level: [],
  q: '',
  sort: 'lastSeen',
};

interface VendorBannerProps {
  vendor: 'sentry' | 'posthog';
  status: VendorStatus;
  envVar: string;
}

function VendorBanner({ vendor, status, envVar }: VendorBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  if (status === 'ok') return null;
  const label = vendor === 'sentry' ? 'Sentry' : 'PostHog';
  const message = status === 'unconfigured'
    ? t('admin.logs.vendorUnconfigured', { label, envVar })
    : t('admin.logs.vendorError', { label });
  return (
    <div className={`admin-logs-banner admin-logs-banner--${status}`} role="status">
      {message}
    </div>
  );
}

export function LogsPage(): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState<LogQuery>(DEFAULT_QUERY);
  const [resp, setResp] = useState<LogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inflightCounter = useRef(0);

  const fetchLogs = useCallback(async (q: LogQuery, bust = false): Promise<void> => {
    const myId = ++inflightCounter.current;
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.listLogs({ ...q, bust });
      // Drop responses that lost the race.
      if (myId !== inflightCounter.current) return;
      setResp(result);
      posthog.captureEvent('admin_logs_viewed', {
        vendorStatus: result.vendorStatus,
        rowCount: result.rows.length,
        since: q.since,
        source: q.source,
      });
    } catch (err) {
      if (myId !== inflightCounter.current) return;
      setError(err instanceof Error ? err.message : t('admin.logs.loadError'));
    } finally {
      if (myId === inflightCounter.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLogs(query);
  }, [query, fetchLogs]);

  const onChange = useCallback((patch: Partial<LogQuery>): void => {
    setQuery((prev) => ({ ...prev, ...patch }));
  }, []);

  const onRefresh = useCallback((): void => {
    void fetchLogs(query, true);
  }, [fetchLogs, query]);

  const onSortChange = useCallback((sort: LogQuery['sort']): void => {
    setQuery((prev) => ({ ...prev, sort }));
  }, []);

  const bothDown = useMemo(
    () => resp !== null && resp.vendorStatus.sentry !== 'ok' && resp.vendorStatus.posthog !== 'ok',
    [resp],
  );

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.logs.title')}</h1>
        <p className="muted">
          {t('admin.logs.blurb')}
        </p>
      </header>

      <section className="admin-card">
        <LogsFilterBar
          query={query}
          onChange={onChange}
          onRefresh={onRefresh}
          isBusy={loading}
        />

        {resp && (
          <>
            <VendorBanner vendor="sentry" status={resp.vendorStatus.sentry} envVar="SENTRY_AUTH_TOKEN" />
            <VendorBanner vendor="posthog" status={resp.vendorStatus.posthog} envVar="POSTHOG_PERSONAL_API_KEY" />
          </>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        {bothDown && (
          <div className="admin-logs-emptystate">
            <p className="muted">{t('admin.logs.bothDown')}</p>
            <button type="button" className="btn" onClick={onRefresh} disabled={loading}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {loading && resp === null && <p className="muted" role="status">{t('admin.logs.loading')}</p>}

        {resp && !bothDown && (
          <LogsTable rows={resp.rows} sort={query.sort} onSortChange={onSortChange} />
        )}
      </section>
    </main>
  );
}
